import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import DialogContent from '@material-ui/core/DialogContent';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import createStyles from '@material-ui/core/styles/createStyles';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Info from 'components/Tooltip/Info';
import FreeSwitch from 'components/FormFields/FreeSwitch';

import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { RootStore } from 'stores/rootStore';
import { ClientView, Method2FA, Request } from 'enums';

const useRadioStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > div > div': {
        display: 'flex',
        alignItems: 'center',
      },
      '& .MuiFormControl-root': {
        marginRight: theme.spacing(1),
      },
    },
  }),
);

interface Props {
  rootStore: RootStore;
  accountDetailStore: AccountDetailStore;
  openClientView: Function;
}

const ClientViewDialog: React.FC<Props> = ({ rootStore, accountDetailStore, openClientView }) => {
  const { t } = useTranslation();
  const radioClasses = useRadioStyles();
  const [clientAccess, setClientAccess] = useState('');
  const currentView = accountDetailStore.data.summary?.ownerData.clientView || '';
  const current2fa = !!accountDetailStore.data.summary?.ownerData.use2fa;
  const [use2fa, setUse2fa] = useState(current2fa);
  const currentMethod = accountDetailStore.data.summary?.ownerData.method || Method2FA.NONE;
  const [method2fa, setMethod2fa] = useState(currentMethod);
  const reactivationEnabled = accountDetailStore.data.summary?.ownerData.reactivationEnabled;
  const resetEnabled = accountDetailStore.data.summary?.ownerData.resetEnabled;
  const { clientViewEnabled } = rootStore.stores.uiStore.userMenuData;
  const [confirmAction, setConfirmAction] = useState<'VIEW' | '2FA' | 'METHOD' | null>(null);
  const pendingRequest =
    accountDetailStore.pendingRequests.includes(Request.TWOFACTOR_ENABLE) ||
    accountDetailStore.pendingRequests.includes(Request.TWOFACTOR_DISABLE);

  useEffect(() => setClientAccess(currentView), [currentView]);

  return (
    <>
      <Dialog open={true} maxWidth="sm">
        <DialogHeader onClose={() => openClientView(false)}>
          {t(
            `client_sendPortalActivation${
              currentView !== '' && !reactivationEnabled && !resetEnabled ? '_update' : ''
            }`,
          )}
        </DialogHeader>
        <DialogContent>
          <Typography>
            {t(
              `client_sendPortalActivation_info${
                currentView !== '' && !reactivationEnabled && !resetEnabled ? '_update' : ''
              }`,
            )}
          </Typography>
          <Grid container alignItems="center">
            <Grid item xs={12} />
            <Grid item xs={12}>
              {t('client_access')}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset" className={radioClasses.root}>
                <RadioGroup name="access" onChange={(e) => setClientAccess(e.target.value)} value={clientAccess}>
                  <div>
                    <FormControlLabel
                      value={ClientView.BASIC}
                      control={<Radio />}
                      label={t(`client_access_${ClientView.BASIC}`)}
                      disabled={!clientViewEnabled}
                    />
                    <Info content={t('client_access_BASIC_tooltip')} inline />
                  </div>
                  <div>
                    <FormControlLabel
                      value={ClientView.FULL}
                      control={<Radio />}
                      label={t(`client_access_${ClientView.FULL}`)}
                      disabled={!clientViewEnabled}
                    />
                    <Info
                      content={
                        <>
                          {t('client_access_FULL_tooltip')} <strong>{t('client_access_FULL2FA_tooltip')}</strong>
                        </>
                      }
                      inline
                    />
                  </div>
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid container item xs={12} sm={6} justifyContent="flex-end" alignItems="center">
              <Button
                onClick={() => setConfirmAction('VIEW')}
                variant="contained"
                color="primary"
                disabled={
                  (!clientViewEnabled && clientAccess === '') ||
                  (clientAccess === currentView && !reactivationEnabled && !resetEnabled)
                }
              >
                {t('general_set')}
              </Button>
            </Grid>
            <Grid item xs={12} />
            <Grid item xs={12} sm={8}>
              <FreeSwitch
                id={`use2fa`}
                label={`${t('account_2fa')}`}
                tooltipContent={
                  <>
                    {t('tooltip_account_2fa')}
                    {currentView === ClientView.FULL && (
                      <>
                        <br />
                        <strong>{t('tooltip_account_2fa_basicView')}</strong>
                      </>
                    )}
                  </>
                }
                checked={use2fa}
                afterChange={() => setUse2fa(!use2fa)}
                disabled={currentView === ClientView.FULL}
              />
            </Grid>
            <Grid container item xs={12} sm={4} justifyContent="flex-end" alignItems="center">
              <Button
                onClick={() => setConfirmAction('2FA')}
                variant="contained"
                color="primary"
                disabled={use2fa === current2fa}
              >
                {t('general_send')}
              </Button>
            </Grid>

            {use2fa && current2fa && (
              <>
                <Grid item xs={12} sm={8}>
                  <TextField
                    select={true}
                    value={method2fa}
                    onChange={(e) => setMethod2fa(e.target.value as Method2FA)}
                    label={t('account_2fa_method')}
                  >
                    {['OTP', 'SMS'].map((m) => (
                      <MenuItem key={m} value={m}>
                        {t(`account_2fa_method_${m}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid container item xs={12} sm={4} justifyContent="flex-end" alignItems="center">
                  <Button
                    onClick={() => setConfirmAction('METHOD')}
                    variant="contained"
                    color="primary"
                    disabled={method2fa === currentMethod}
                  >
                    {t('general_confirm')}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
      {confirmAction && (
        <ConfirmDialog
          isOpen={true}
          confirm={async () => {
            if (confirmAction === 'VIEW') {
              await accountDetailStore.sendPortalActivation(
                clientAccess as ClientView,
                currentView !== '' && !reactivationEnabled && !resetEnabled,
              );
            } else if (confirmAction === '2FA') {
              await accountDetailStore.set2fa(use2fa);
            } else if (confirmAction === 'METHOD') {
              await accountDetailStore.set2faMethod(method2fa);
            }
            setConfirmAction(null);
            openClientView(false);
          }}
          close={() => setConfirmAction(null)}
          btnConfirmText={pendingRequest ? 'general_send' : ''}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                confirmAction === 'METHOD'
                  ? 'account_method_confirmChange'
                  : confirmAction === 'VIEW'
                  ? 'client_access_confirmChange'
                  : pendingRequest
                  ? 'account_2fa_confirmChange_pending'
                  : 'account_2fa_confirmChange',
                {
                  interpolation: { escapeValue: false },
                },
              ),
            }}
          />
        </ConfirmDialog>
      )}
    </>
  );
};

export default ClientViewDialog;
